import _ from 'lodash';
import moment, { Moment } from 'moment';
import { GROWTH_CALCULATORS } from 'spw-constants';
import { FileType } from 'spw-redux/modules/reports/types';
import { PlainProgramScenario } from '../../../models/Scenario';
import { DEFAULT_DOWNLOAD_OPTIONS } from './constants';
import {
  DownloadOptions,
  DownloadTypes,
  RowsVisibility,
  DownloadTemplateVM,
  DownloadConfig,
  DownloadTemplate,
} from './types';

export const formatDownloadDateTime = (dateTime: Moment) => {
  const now = moment().local();
  const yesterday = now.clone().subtract(1, 'days').startOf('day');
  const downloadDateTime = moment.utc(dateTime).local();

  let date = downloadDateTime.format('MM/DD/YYYY');

  if (downloadDateTime.isSame(now, 'd')) {
    date = 'Today';
  } else if (downloadDateTime.isSame(yesterday, 'd')) {
    date = 'Yesterday';
  }

  return `${date} at ${dateTime.local().format('hh:mm A')}`;
};

export const formatTemplateDateTime = (dateTime: string) => {
  return moment.utc(dateTime).local().format('MM/DD/YYYY');
};

export const isRowVisibility = (
  optionValue: any
): optionValue is RowsVisibility => {
  return (
    optionValue.shouldRowsShowClosed !== undefined ||
    optionValue.shouldRowsShowOpen !== undefined
  );
};

export const getOnlyChangedProperties = (
  previousOptionValue: RowsVisibility | boolean,
  optionValue: RowsVisibility | boolean
) => {
  if (typeof optionValue === 'boolean') return optionValue;
  const output: RowsVisibility = { ...optionValue };
  Object.keys(optionValue).forEach((key) => {
    if (
      isRowVisibility(previousOptionValue) &&
      optionValue[key as keyof RowsVisibility] ===
        previousOptionValue[key as keyof RowsVisibility]
    ) {
      delete output[key as keyof RowsVisibility];
    }
  });
  return output;
};

export const getNumberOfSelectedOptions = (options: DownloadOptions) => {
  if (!options) return 0;
  return Object.keys(options).reduce((count, key: DownloadTypes) => {
    const optionValue = options[key];
    if (isRowVisibility(optionValue))
      return (
        count +
        (optionValue.shouldRowsShowClosed && 1) +
        (optionValue.shouldRowsShowOpen && 1)
      );

    return optionValue ? count + 1 : count;
  }, 0);
};

export const areAllOptionsSelected = (
  selectedOptions: DownloadOptions,
  isScenarioWithoutNestedRows: boolean,
  isScenarioWithoutNotes: boolean
) => {
  let numberOfAvailableOptions = 9;
  if (isScenarioWithoutNestedRows && isScenarioWithoutNotes)
    numberOfAvailableOptions = 4;
  if (isScenarioWithoutNotes && !isScenarioWithoutNestedRows)
    numberOfAvailableOptions = 6;
  if (isScenarioWithoutNestedRows && !isScenarioWithoutNotes)
    numberOfAvailableOptions = 6;

  return (
    getNumberOfSelectedOptions(selectedOptions) === numberOfAvailableOptions
  );
};

type RowVisibilityCount = {
  shouldRowsShowOpen: number;
  shouldRowsShowClosed: number;
};

const isRowVisibilityCount = (
  optionValue: any
): optionValue is RowVisibilityCount => {
  return optionValue.shouldRowsShowClosed !== undefined;
};

export const getScenarioTypeSelectedOptions = (
  scenarioTypeScenariosOptions: DownloadOptions[],
  scenariosWithoutNestedRowsCount: number,
  scenariosWithoutNotesCount: number,
  scenariosWithNestedRowsAndNoNotesCount: number
) => {
  const scenarioTypeScenariosLength =
    scenarioTypeScenariosOptions.length || null;
  const scenarioTypeOptionsCount: {
    [key in DownloadTypes]: number | RowVisibilityCount;
  } = {
    seat_count_analysis: 0,
    summary_calcs: 0,
    worksheet: { shouldRowsShowClosed: 0, shouldRowsShowOpen: 0 },
    worksheet_with_notes: { shouldRowsShowClosed: 0, shouldRowsShowOpen: 0 },
    program_summary: { shouldRowsShowClosed: 0, shouldRowsShowOpen: 0 },
    program_notes: 0,
  };
  scenarioTypeScenariosOptions.forEach((options) => {
    if (!options) return;
    Object.keys(options).forEach((key: DownloadTypes) => {
      const optionValue = options[key];
      if (isRowVisibility(optionValue)) {
        (scenarioTypeOptionsCount[
          key
        ] as RowVisibilityCount).shouldRowsShowClosed += Number(
          optionValue.shouldRowsShowClosed
        );
        (scenarioTypeOptionsCount[
          key
        ] as RowVisibilityCount).shouldRowsShowOpen += Number(
          optionValue.shouldRowsShowOpen
        );
      } else {
        (scenarioTypeOptionsCount[key] as number) += Number(options[key]);
      }
    });
  });
  const numberOfScenariosWithNestedRows =
    scenarioTypeScenariosLength &&
    scenarioTypeScenariosLength - scenariosWithoutNestedRowsCount;
  const scenariosWithNotesCount =
    scenarioTypeScenariosLength &&
    scenarioTypeScenariosLength - scenariosWithoutNotesCount;

  const scenarioTypeDownloadOptions = Object.keys(
    scenarioTypeOptionsCount
  ).reduce((options, key: DownloadTypes) => {
    const optionValue = scenarioTypeOptionsCount[key];
    const isOptionForNotes = key.indexOf('notes') !== -1;
    if (isRowVisibilityCount(optionValue)) {
      if (isOptionForNotes && scenariosWithNotesCount === 0)
        return {
          ...options,
          [key]: { shouldRowsShowClosed: null, shouldRowsShowOpen: null },
        };
      if (isOptionForNotes) {
        return {
          ...options,
          [key]: {
            shouldRowsShowClosed:
              optionValue.shouldRowsShowClosed === scenariosWithNotesCount,
            shouldRowsShowOpen:
              scenariosWithNestedRowsAndNoNotesCount > 0
                ? optionValue.shouldRowsShowOpen ===
                  scenariosWithNestedRowsAndNoNotesCount
                : null,
          },
        };
      }
      return {
        ...options,
        [key]: {
          shouldRowsShowClosed:
            optionValue.shouldRowsShowClosed === scenarioTypeScenariosLength,
          shouldRowsShowOpen:
            numberOfScenariosWithNestedRows > 0 || !scenarioTypeScenariosLength
              ? optionValue.shouldRowsShowOpen ===
                numberOfScenariosWithNestedRows
              : null,
        },
      };
    }
    let value = scenarioTypeOptionsCount[key] === scenarioTypeScenariosLength;
    if (isOptionForNotes)
      value =
        scenariosWithNotesCount === 0
          ? null
          : scenarioTypeOptionsCount[key] === scenariosWithNotesCount;

    return {
      ...options,
      [key]: value,
    };
  }, DEFAULT_DOWNLOAD_OPTIONS);
  return scenarioTypeDownloadOptions;
};

const {
  floorHeadcountSeatcount,
  floorUsf,
  totalProgram,
  buildingHeadcountSeatcountDepartments,
  macroProgramming,
  buildingHeadcountSeatcount,
  buildingUsf,
} = GROWTH_CALCULATORS;

export const getScenarioLabels = (
  scenario: PlainProgramScenario
): { primaryLabel: string; secondaryLabel: string } => {
  if (scenario.calculatorType === totalProgram)
    return {
      primaryLabel: '',
      secondaryLabel: `${scenario.linkedScenarios?.length} linked scenarios`,
    };
  if ([floorHeadcountSeatcount, floorUsf].includes(scenario.calculatorType))
    return {
      primaryLabel: scenario.primaryLabel,
      secondaryLabel: '',
    };
  if (scenario.calculatorType === buildingHeadcountSeatcountDepartments)
    return {
      primaryLabel: scenario.primaryLabel,
      secondaryLabel: scenario.secondaryLabel,
    };
  return {
    primaryLabel: '',
    secondaryLabel: '',
  };
};

export const getDefaultOptions = (
  areAllChildrenSelected: boolean,
  isScenarioWithoutNestedRows: boolean,
  isScenarioWithoutNotes: boolean
) => {
  if (!areAllChildrenSelected) return undefined;

  return _.cloneDeep({
    seat_count_analysis: true,
    summary_calcs: true,
    worksheet: {
      shouldRowsShowClosed: true,
      shouldRowsShowOpen: !isScenarioWithoutNestedRows,
    },
    worksheet_with_notes: {
      shouldRowsShowClosed: !isScenarioWithoutNotes,
      shouldRowsShowOpen:
        !isScenarioWithoutNestedRows && !isScenarioWithoutNotes,
    },
    program_summary: {
      shouldRowsShowClosed: true,
      shouldRowsShowOpen: !isScenarioWithoutNestedRows,
    },
    program_notes: !isScenarioWithoutNotes,
  });
};

export const getUniqueKey = (entityId: number, isTotalProgram?: boolean) =>
  `${isTotalProgram ? 'totalProgramId' : 'scenarioId'}:${entityId}`;

export const parseUITemplateToConfig = (
  downloadTemplate: DownloadTemplateVM
): DownloadConfig[] => {
  const downloadConfigs: DownloadConfig[] = [];
  Object.keys(downloadTemplate.config).forEach((fileType: FileType) => {
    Object.keys(downloadTemplate.config[fileType]).forEach((scenarioKey) => {
      const entityType = scenarioKey.split(':')[0];
      const entityId: number = parseInt(scenarioKey.split(':')[1], 10);
      Object.keys(downloadTemplate.config[fileType][scenarioKey]).forEach(
        (downloadConfigType: DownloadTypes) => {
          const configValue =
            downloadTemplate.config[fileType][scenarioKey][downloadConfigType];
          if (!isRowVisibility(configValue) && !configValue) return; // Shouldn't create the record
          if (
            isRowVisibility(configValue) &&
            !configValue.shouldRowsShowClosed &&
            !configValue.shouldRowsShowOpen
          )
            return; // Shouldn't create the record
          const detailedConfig = isRowVisibility(configValue)
            ? configValue
            : null;

          downloadConfigs.push({
            downloadConfigType,
            downloadFileType: fileType,
            [entityType]: entityId,
            detailedConfig: detailedConfig as RowsVisibility,
          });
        }
      );
    });
  });
  return downloadConfigs;
};

export const parseDownloadTemplateToVM = (
  downloadTemplate: DownloadTemplate
): DownloadTemplateVM => {
  const config: {
    [key in FileType]: Record<string | number, DownloadOptions>;
  } = {
    pdf: {},
    png: {},
  };
  const copyOfDownloadTemplate = JSON.parse(JSON.stringify(downloadTemplate));
  delete copyOfDownloadTemplate.downloadConfigs;
  downloadTemplate.downloadConfigs.forEach((dc) => {
    const entityId = dc.scenarioId || dc.totalProgramId;
    const doesConfigBelongToTotalProgram = !!dc.totalProgramId;
    const uniqueKey = getUniqueKey(entityId, doesConfigBelongToTotalProgram);
    if (!config[dc.downloadFileType][uniqueKey])
      config[dc.downloadFileType][uniqueKey] = {} as DownloadOptions;
    config[dc.downloadFileType][uniqueKey][dc.downloadConfigType] =
      dc.detailedConfig || true;
  });
  return {
    ...copyOfDownloadTemplate,
    config,
  };
};

export const CALCULATOR_TYPE_TO_CONSOLIDATED_SCENARIO_TYPE = {
  [macroProgramming]: 'macro',
  [buildingHeadcountSeatcount]: 'building',
  [buildingUsf]: 'building',
  [buildingHeadcountSeatcountDepartments]: 'department',
  [floorHeadcountSeatcount]: 'floor',
  [floorUsf]: 'floor',
  [totalProgram]: 'totalProgram',
};

export const getInitiallyExpandedScenarioTypes = (
  sourceTemplate: DownloadTemplateVM,
  scenarios: PlainProgramScenario[],
  allLinkedDepartmentsMap: { [scenarioId: number]: number }
) => {
  const scenarioTypesToExpandMap: Record<
    FileType,
    { [scenarioType: string]: boolean }
  > = { pdf: {}, png: {} };
  const parentsToExpandMap: { [totalProgramKey: string]: boolean } = {};
  Object.keys(sourceTemplate.config).forEach((fileType: FileType) => {
    Object.keys(sourceTemplate.config[fileType]).forEach(
      (scenarioKey: string) => {
        // scenarioIdType can be either "totalProgramId" or "scenarioId"
        const [scenarioIdType, scenarioId] = scenarioKey.split(':');
        if (scenarioIdType === 'totalProgramId') {
          scenarioTypesToExpandMap[fileType].totalProgram = true;
        } else {
          const scenario = scenarios.find(
            (s) => s.id.toString() === scenarioId
          );
          if (scenario) {
            const { calculatorType } = scenario;
            scenarioTypesToExpandMap[fileType][
              CALCULATOR_TYPE_TO_CONSOLIDATED_SCENARIO_TYPE[calculatorType]
            ] = true;
            if (allLinkedDepartmentsMap[scenario.id]) {
              const parentTPSId = allLinkedDepartmentsMap[scenario.id];
              parentsToExpandMap[`totalProgramId:${parentTPSId}`] = true;
            }
          }
        }
      }
    );
  });
  return { scenarioTypesToExpandMap, parentsToExpandMap };
};

export const stripAllFalsyOptions = (options: DownloadOptions) => {
  if (!options) return options;
  let allOptionsAreFalsy = true;
  Object.keys(options).forEach((key) => {
    const value = options[key as keyof DownloadOptions];
    if (
      isRowVisibility(value) &&
      (value.shouldRowsShowClosed || value.shouldRowsShowOpen)
    ) {
      allOptionsAreFalsy = false;
    } else if (typeof value === 'boolean' && value) {
      allOptionsAreFalsy = false;
    } else {
      delete options[key as keyof DownloadOptions];
    }
  });
  if (allOptionsAreFalsy) {
    return undefined;
  }
  return options;
};
